import { useMemo } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  IconButton,
  Box,
  Tooltip,
  LinearProgress,
  Typography,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { UploadedFileInfo } from '../../../types/generatedTypes';
import { DataFeedRow } from '../types';
import { TOTAL_STEPS } from '../../DataFeedWizard/utils/stepUtils';

interface UseDataFeedColumnsProps {
  onDelete: (sessionId: string, fileName: string) => void;
}

export const useDataFeedColumns = ({ onDelete }: UseDataFeedColumnsProps) => {
  const navigate = useNavigate();

  const handleResume = (sessionId: string) => {
    navigate(`/data-feed-wizard?session_id=${sessionId}`);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'flowName',
        headerName: 'Flow Name',
        flex: 1,
        sortable: true,
        valueFormatter: (value: string | null) => {
          if (!value) return '-';
          return value;
        },
      },
      {
        field: 'fileType',
        headerName: 'File Type',
        width: 100,
        sortable: true,
      },
      {
        field: 'uploadedFiles',
        headerName: 'Uploaded Files',
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const files = params.value as UploadedFileInfo[];
          if (!files || files.length === 0) return null;

          // Helper to get display name (prefer output_filename for vaults)
          const getDisplayName = (f: UploadedFileInfo) =>
            f.output_filename ?? f.filename;

          if (files.length === 1) {
            return getDisplayName(files[0]);
          }

          const tooltipContent = files.map(getDisplayName).join('\n');

          return (
            <Tooltip
              title={
                <span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>
              }
            >
              <span>{files.length} files</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        flex: 0.8,
        sortable: true,
        valueFormatter: (value: string) => {
          if (!value) return '';
          const date = new Date(value);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'uploadDate',
        headerName: 'Last Update',
        flex: 0.8,
        sortable: true,
        valueFormatter: (value: string) => {
          if (!value) return '';
          const date = new Date(value);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },

      {
        field: 'uploadProgress',
        headerName: 'Upload Progress',
        flex: 0.8,
        sortable: true,
        sortComparator: (_v1, _v2, param1, param2) => {
          const row1 = param1.api.getRow(param1.id) as DataFeedRow;
          const row2 = param2.api.getRow(param2.id) as DataFeedRow;

          // Calculate progress ratios
          const ratio1 =
            row1.enabledRowsCount > 0
              ? row1.uploadedRowsCount / row1.enabledRowsCount
              : 0;
          const ratio2 =
            row2.enabledRowsCount > 0
              ? row2.uploadedRowsCount / row2.enabledRowsCount
              : 0;

          return ratio1 - ratio2;
        },
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as DataFeedRow;
          const uploaded = row.uploadedRowsCount;
          const enabled = row.enabledRowsCount;

          if (enabled === 0) return null;

          const progress = (uploaded / enabled) * 100;
          const isComplete = uploaded === enabled;

          return (
            <Box
              data-testid={`progress-cell-${row.sessionId}`}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="caption">
                {uploaded.toLocaleString()}/{enabled.toLocaleString()}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: isComplete
                      ? 'success.main'
                      : 'primary.main',
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Current Step',
        flex: 1,
        sortable: true,
        sortComparator: (_v1, _v2, param1, param2) => {
          const row1 = param1.api.getRow(param1.id) as DataFeedRow;
          const row2 = param2.api.getRow(param2.id) as DataFeedRow;

          // Check if complete (all rows uploaded)
          const isComplete1 =
            (row1.enabledRowsCount > 0 &&
              row1.uploadedRowsCount === row1.enabledRowsCount) ||
            row1.isComplete;
          const isComplete2 =
            (row2.enabledRowsCount > 0 &&
              row2.uploadedRowsCount === row2.enabledRowsCount) ||
            row2.isComplete;

          // Complete items sort last (so they appear first when descending)
          if (isComplete1 && !isComplete2) return 1;
          if (!isComplete1 && isComplete2) return -1;

          // Then sort by step number
          return row1.currentStep - row2.currentStep;
        },
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as DataFeedRow;
          const status = params.value as string;
          if (!status) return null;

          const totalSteps = TOTAL_STEPS;
          const allRowsUploaded =
            row.enabledRowsCount > 0 &&
            row.uploadedRowsCount === row.enabledRowsCount;
          const isComplete = allRowsUploaded || row.isComplete;
          const stepText = isComplete
            ? 'Complete'
            : `Step ${row.currentStep}/${totalSteps} - ${status}`;

          return (
            <Chip
              data-testid={`status-chip-${row.sessionId}`}
              icon={isComplete ? <CheckIcon /> : undefined}
              label={stepText}
              color={isComplete ? 'success' : 'default'}
              size="small"
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as DataFeedRow;

          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!row.isComplete && (
                <IconButton
                  data-testid={`resume-btn-${row.sessionId}`}
                  size="small"
                  color="primary"
                  onClick={() => handleResume(row.sessionId)}
                  title="Resume"
                >
                  <EditIcon />
                </IconButton>
              )}
              <IconButton
                data-testid={`delete-btn-${row.sessionId}`}
                size="small"
                color="error"
                onClick={() => onDelete(row.sessionId, row.fileName)}
                title="Delete session and file"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [navigate, onDelete]
  );

  return columns;
};
